import React, { useState, useContext, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  ModalFooter,
  Input,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Col,
  FormGroup,
  Label,
} from 'src/components/ui';
import DOMPurify from 'dompurify';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import JsPDF from 'jspdf';
import PopupNotificationApi from '../api/PopupNotificationApi';
import { AuthContext } from '../context/AuthContext';
import useResourceLoader from '../util/useResourceLoader';
import FileApi from '../api/FileApi';
import Loader from './Loader';
import { useTheme } from '@emotion/react';
import { Trans } from '@lingui/react/macro';

function PopupNotification(props) {
  const { isPreview = false, prevNotification = null, setPreviewNotif } = props;
  const theme = useTheme();
  const auth = useContext(AuthContext);

  const [notifications, loadingNotifications = true] = useResourceLoader(() => (auth?.user && !isPreview ? PopupNotificationApi.check() : null), [
    auth,
    auth?.user,
  ]);

  const [isOpen, setIsOpen] = useState(true);
  const [page, setPage] = useState(0);

  const [checked, setChecked] = useState(false);
  const [checkedError, setCheckedError] = useState(null);

  const bodyRef = useRef(null);
  const [seenPages, setSeenPages] = useState(new Set());

  const [currentNotification, setCurrentNotification] = useState(0);

  const notification = useMemo(() => {
    if (isPreview && prevNotification) {
      return prevNotification;
    }
    if (notifications) {
      return notifications[currentNotification];
    }
    return null;
  }, [notifications, currentNotification, prevNotification, isPreview]);

  const goToNextPage = () => {
    if (page < notification?.pages.length - 1) {
      setPage(page + 1);
    }
  };

  const goToPreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const nextNotification = () => {
    const valueForValidate = currentNotification + 1;
    if (notifications?.[valueForValidate]) {
      setCurrentNotification(prev => prev + 1);
      setPage(0);
    } else {
      setIsOpen(false);
    }
  };

  async function submitNotification(notification) {
    if (notification) {
      if (notification.requiresConfirmation && !checked) {
        setCheckedError('Debe confirmar la notificación');
      } else {
        setCheckedError(null);
        setChecked(false);
        nextNotification();
        await PopupNotificationApi.confirm(notification.id);
      }
    }
  }

  const onClosePreview = () => {
    setPreviewNotif(null);
    setCurrentNotification(0);
    setPage(0);
    setChecked(false);
  };

  const [activeTab, setActiveTab] = useState('notification');

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const [downloading, setDownloading] = useState(false);

  // Hacky way of setting modal header color
  const color = notification?.title === 'Aviso de cancelación en señalamientos' ? 'warning' : 'primary';
  const headerStyle = {
    backgroundColor: theme[`${color}Main`],
    color: theme[`primayTextMain`],
  };

  const sanitizedHTML = useMemo(
    () => ({
      __html: DOMPurify.sanitize(notification?.pages?.[page]?.content),
    }),
    [notification?.pages?.[page]?.content]
  );

  // Reset seen pages when moving to a different notification
  useEffect(() => {
    setSeenPages(new Set());
  }, [currentNotification, prevNotification]);

  // On each page change: scroll to top, then check if content fits without scrolling
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = 0;

    const timer = setTimeout(() => {
      const el = bodyRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 5) {
        setSeenPages(prev => new Set([...prev, page]));
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [page, notification]);

  // Re-check whether content fits whenever the window is resized
  useEffect(() => {
    const handleResize = () => {
      const el = bodyRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 5) {
        setSeenPages(prev => (prev.has(page) ? prev : new Set([...prev, page])));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [page]);

  const handleBodyScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 5) {
      setSeenPages(prev => new Set([...prev, page]));
    }
  };

  const allPagesSeen = useMemo(() => {
    if (!notification?.requiresConfirmation) return true;
    const totalPages = notification?.pages?.length ?? 1;
    for (let i = 0; i < totalPages; i++) {
      if (!seenPages.has(i)) return false;
    }
    return true;
  }, [notification, seenPages]);

  const pageCount = notification?.pages?.length ?? 0;
  const isLastPage = pageCount <= 1 || page === pageCount - 1;

  const handleClose = isPreview ? onClosePreview : !notification?.requiresConfirmation ? () => submitNotification(notification) : undefined;

  return (
    ((notifications && !loadingNotifications && notification) || (isPreview && prevNotification)) && (
      <Modal isOpen={isOpen} size="xl" backdrop="static">
        <ModalHeader color="primary" className="modal-header-notification" style={headerStyle} toggle={handleClose}>
          <div className="flex justify-between">
            <div className="p-2 notifications-title">{notification?.title}</div>
            {notification?.pages?.length > 1 && (
              <small className="p-2 text-right">
                <Trans>Page</Trans> {page + 1}/{notification?.pages?.length}
              </small>
            )}
          </div>
        </ModalHeader>
        <ModalBody className="ql-viewer">
          {downloading ? <Loader fullscreen /> : null}
          <div ref={bodyRef} onScroll={handleBodyScroll} style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 'calc(100vh - 300px)' }}>
            {notification?.uploadedFiles?.length > 0 ? (
              <>
                <Nav tabs className="notification-tabs">
                  <NavItem>
                    <NavLink
                      style={{ border: activeTab === 'files' ? 'none' : '', fontWeight: activeTab === 'notification' ? 'bold' : '' }}
                      onClick={() => {
                        toggleTab('notification');
                      }}
                    >
                      <Trans>Notification</Trans>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{ border: activeTab === 'notification' ? 'none' : '', fontWeight: activeTab === 'files' ? 'bold' : '' }}
                      onClick={() => {
                        toggleTab('files');
                      }}
                    >
                      <Trans>Files</Trans>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="notification">
                    <Row>
                      <Col sm="12">
                        <div dangerouslySetInnerHTML={sanitizedHTML} />
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="files">
                    <Row>
                      <Col sm="12">
                        {notification?.uploadedFiles?.map((item, key) => {
                          return <FileDownload key={item.id} item={item} index={key} />;
                        })}
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </>
            ) : (
              <Row>
                <Col sm="12">
                  <div dangerouslySetInnerHTML={sanitizedHTML} />
                </Col>
              </Row>
            )}
          </div>
        </ModalBody>
        {(notification?.requiresConfirmation || pageCount > 1) && (
          <ModalFooter className="justify-between">
            {notification?.requiresConfirmation && isLastPage && (
              <div className="col-lg-12 mb-0 flex" style={{ flexDirection: 'column', textAlign: 'right' }}>
                <div style={{ width: 'min-content', alignSelf: 'flex-end' }}>
                  <FormGroup
                    check
                    className=" btn btn-warning py-2 pr-4 pl-5 ghost-checkbox-wrap"
                    onClick={allPagesSeen ? () => setChecked(!checked) : undefined}
                    style={{ opacity: allPagesSeen ? 1 : 0.5, cursor: allPagesSeen ? 'pointer' : 'not-allowed' }}
                  >
                    <Label check className="ghost-checkbox-wrap ml-4" for={`${notification.id}`} style={{ pointerEvents: 'none' }}>
                      <Input type="checkbox" className="ghost-checkbox-input" id={`${notification.id}`} checked={checked} disabled={!allPagesSeen} />
                      <span className="ghost-checkbox-box" />
                      {notification?.requiresConfirmationText}
                    </Label>
                  </FormGroup>
                </div>
                {checkedError ? <div className="text-danger">{checkedError}</div> : null}
              </div>
            )}
            {activeTab !== 'files' && (
              <div className="col-lg-12 mt-4">
                {page > 0 && (
                  <Button className="float-left mr-3" onClick={goToPreviousPage} color="primary">
                    <Trans>Previous Page</Trans>
                  </Button>
                )}
                {notification?.pages.length - 1 !== page && (
                  <Button className="float-left" onClick={goToNextPage} color="primary">
                    <Trans>Next Page</Trans>
                  </Button>
                )}
                {!isPreview && notification?.requiresConfirmation && isLastPage && (
                  <Button
                    className="float-right"
                    onClick={() => submitNotification(notification)}
                    color="primary"
                    disabled={notification.requiresConfirmation && !checked}
                  >
                    <Trans>Confirm</Trans>
                  </Button>
                )}
                {isPreview && (
                  <Button className="float-right" onClick={() => onClosePreview()} color="primary">
                    <Trans>Close</Trans>
                  </Button>
                )}
                <Button
                  className="float-right mr-2"
                  onClick={() => handleDownloadZip({ files: notification?.uploadedFiles, notification, setDownloading })}
                  color="secondary"
                >
                  <Trans>Download</Trans>
                </Button>
              </div>
            )}
          </ModalFooter>
        )}
      </Modal>
    )
  );
}

const FileDownload = ({ item, index }) => {
  const handleDownload = fileUrl => {
    const a = document.createElement('a');
    a.href = fileUrl;
    a.target = '_blank';
    a.click();
  };

  return (
    <span
      key={index}
      className={`file-link ${index === 0 ? 'pt-2' : ''}`}
      tabIndex={index}
      role="link"
      onClick={async () => {
        const file = await FileApi.getFile({ fileId: item.id });
        if (file) {
          handleDownload(file.url, item.name);
        }
      }}
    >
      {item.name}
    </span>
  );
};

const generatePDFDocument = async notification => {
  let content = `<h4 style="margin-bottom:20px">${notification?.title}</h4>`;

  let response = null;

  let i = 0;
  for (i; i < notification?.pages?.length; i += 1) {
    content += notification?.pages?.[i]?.content;
  }

  if (!content) return null;

  const tempDiv = document.createElement('div');
  tempDiv.style.paddingRight = '20mm';
  tempDiv.style.paddingBottom = '20mm';
  tempDiv.style.width = '210mm';
  tempDiv.innerHTML = content;

  const images = tempDiv.getElementsByTagName('img');

  const iframes = tempDiv.getElementsByTagName('iframe');

  const maxWidth = '190mm';

  for (let i = 0; i < images.length; i += 1) {
    const img = images[i];
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    const aspectRatio = naturalWidth / naturalHeight;

    const newWidth = Math.min(naturalWidth, parseFloat(maxWidth));
    const newHeight = newWidth / aspectRatio;

    img.style.maxWidth = `${newWidth}mm`;
    img.style.maxHeight = `${newHeight}mm`;
  }

  Array.from(images)?.forEach(img => {
    const originalSrc = img.src;

    if (originalSrc.startsWith('data:image')) return;

    const proxiedSrc = `https://corsproxy.io/?${originalSrc}`;

    img.src = proxiedSrc;
    img.setAttribute('crossorigin', 'anonymous');
  });

  tempDiv.innerHTML = tempDiv.innerHTML;

  for (let i = 0; i < iframes.length; i += 1) {
    const iframe = iframes[i];
    const src = iframe.getAttribute('src');

    if (src) {
      const link = document.createElement('a');
      link.setAttribute('href', src);
      link.textContent = `Video (${src})`;
      iframe.parentNode.replaceChild(link, iframe);
    }
  }

  document.body.appendChild(tempDiv);

  const canvas = await html2canvas(tempDiv, {
    scale: 1,
    useCORS: true,
  });

  if (canvas) {
    tempDiv.remove();

    const contentWidth = canvas.width;
    const contentHeight = canvas.height;

    const pdfPageSize = {
      width: 595,
      height: 1050,
    };

    const totalPages = Math.ceil(contentHeight / pdfPageSize.height);

    const pdf = new JsPDF();

    for (let page = 0; page < totalPages; page += 1) {
      const newCanvas = document.createElement('canvas');
      newCanvas.width = contentWidth;
      newCanvas.height = pdfPageSize.height;

      const ctx = newCanvas.getContext('2d');
      const startY = page * pdfPageSize.height * -1;
      ctx.drawImage(canvas, 0, startY);

      if (page > 0) {
        pdf.addPage();
      }

      pdf.addImage(newCanvas.toDataURL(), 'PNG', 10, 10);
    }

    response = {
      blob: pdf.output('blob'),
      name: `Notification-${notification?.title}.pdf`,
    };
  }

  return response;
};

const handleDownloadZip = async ({ files, notification, setDownloading }) => {
  setDownloading(true);

  const makePdf = await generatePDFDocument(notification);

  if (files?.length > 0) {
    const zip = new JSZip();

    if (makePdf) {
      zip.file(makePdf.name, makePdf.blob);
    }

    const promises = files?.map(async (item, key) => {
      const file = await FileApi.getFile({ fileId: item.id });

      if (file) {
        const dFile = await fetch(file.url);
        const blob = await dFile.blob();
        if (blob) {
          zip.file(item.name, blob);
        }
      }
    });

    await Promise.all(promises);

    const zipBlob = await zip.generateAsync({ type: 'blob' });

    const zipLink = document.createElement('a');
    zipLink.href = URL.createObjectURL(zipBlob);
    zipLink.download = 'files.zip';
    zipLink.click();
  } else {
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(makePdf.blob);
    downloadLink.download = makePdf.name;
    downloadLink.click();
  }

  setDownloading(false);
};

export default PopupNotification;
